import EventListener from "../frontendlib/event_listener";
import { runAsync, type, appendParamObjectToURL } from "../frontendlib/utils";
import {
  EXCHANGE_A9TAM,
  EXCHANGE_ADPOPCORN,
  EXCHANGE_APPLOVIN,
  EXCHANGE_CONNATIX,
  EXCHANGE_INMOBI,
  EXCHANGE_INNERACTIVE,
  EXCHANGE_IRONSOURCE,
  EXCHANGE_MINTEGRAL,
  EXCHANGE_MOMENTO,
  EXCHANGE_NIMBUS,
  EXCHANGE_PANGLE,
  EXCHANGE_SAMSUNG,
  EXCHANGE_TOPON,
  EXCHANGE_VERVE,
  EXCHANGE_VUNGLE,
  EXCHANGE_XIAOMI,
  TRACKING_METHOD_OPEN_METHOD,
  CLICK_OPEN_METHOD_MRAID,
  CLICK_OPEN_METHOD_ANCHOR,
  CLICK_OPEN_METHOD_LOCATION,
} from "../constants";
import Logger from "./logger";
import { Warning } from "../error_handling";

/**
 * This interval is the time between attempts to open a URL via different
 * methods (see `LiftoffMRAID.open`).
 */
const CLICK_RETRY_INTERVAL_MS = 1000;

// Most of exchanges have MRAID detected in code except the IronSource
// We do MRAID open regardless for IronSource according to their request.
const mraidOpenOnlyExchanges = new Set([
  EXCHANGE_A9TAM,
  EXCHANGE_APPLOVIN,
  EXCHANGE_INMOBI,
  EXCHANGE_INNERACTIVE,
  EXCHANGE_IRONSOURCE,
  EXCHANGE_MINTEGRAL,
  EXCHANGE_NIMBUS,
  EXCHANGE_TOPON,
  EXCHANGE_VERVE,
  EXCHANGE_VUNGLE,
]);
// No MRAID is detected and confirmed no MRAID from exchanges
const anchorOpenExchanges = new Set([
  EXCHANGE_ADPOPCORN,
  EXCHANGE_CONNATIX,
  EXCHANGE_MOMENTO,
  EXCHANGE_PANGLE,
  EXCHANGE_SAMSUNG,
]);
// No MRAID is detected and exchanges have not confirmed they support MRAID.
// TODO(cwu): Xiaomi is still in contact. Will be handled after 2025-09-11 meeting.
const mraidOpenUncertainExchanges = new Set([EXCHANGE_XIAOMI]);

/**
 * This is a low-level wrapper around mraid that should be usable generally
 * (i.e. in any HTML ad environment). It's used by both madlib and third-party
 * creatives.
 */
class LiftoffMRAID {
  /**
   * Among other things, this function attaches event listeners to the
   * window.mraid object. It should be called as soon as possible after
   * window.mraid is defined. Because of this side-effect, it should also only
   * be called once.
   */
  static init() {
    this.liftoffMraidInitTimestamp = Date.now();

    EventListener.pubsubify(this);
    this.on = this.on.bind(this);
    this.once = this.once.bind(this);
    this._addMraidEventListener = this._addMraidEventListener.bind(this);

    this.viewableDurationMS = null;
    this.lastViewableTS = null;
    this.clickRetryTimeout = null;
    this.lastOpenedURL = null;
    this.isOrientationLocked = false;
    this.orientationToLock = null;
    this.viewable = null;
    this.isViewableByEvent = null;

    /** Keeps track of each version of window.mraid loaded. */
    this.mraids = [];
    /** This index of this.mraids that is currently loaded on window.mraid. */
    this.mraidIndex = null;

    /**
     * This object keeps track of what the current mraid object supports at the
     * moment support was checked.
     */
    this.supported = {
      methods: {},
      /**
       * NOTE(oscar): Unlike MRAID methods, we log mraid events that we listen
       * to as they occur.
       */
      events: {},
    };

    this._setInitialState();
    this._addEventListeners();
  }

  /** Returns time since LiftoffMRAID has been initialized in milliseconds. */
  static sinceInit() {
    return Date.now() - this.liftoffMraidInitTimestamp;
  }

  static getSupportedLog() {
    return {
      "document.hidden": document.hidden,
      "document.visibilityState": document.visibilityState,
      "LiftoffMRAID.viewable": LiftoffMRAID.viewable,
      MRAID_ENV: window.MRAID_ENV,
      ...this.supported,
    };
  }

  static _setInitialState() {
    const mraid = this.getMRAID();
    this.ready = this._mraidTry(
      () => {
        return this.hasSupport("getState") && mraid.getState() === "default";
      },
      { tag: "getState", fallback: false }
    );

    this.isViewableByEvent = this.hasSupport("isViewable")
      ? this._mraidTry(() => mraid.isViewable(), {
          tag: "isViewable",
          // NOTE(oscar): If isViewable() still fails, we fall back to
          // document.visibilityState.
          fallback: document.visibilityState !== "hidden",
        })
      : document.visibilityState !== "hidden";
    this.processViewableChange(
      this.isViewableByEvent && this.isViewportShown()
    );
    this.viewableDurationMS = 0;
  }

  /** TODO(oscar): Don't append to log if it won't be sent to Adexp. */
  static _appendEventLog(eventName, mraidIndex, args) {
    if (this.supported.events[eventName] == null) {
      this.supported.events[eventName] = [];
    }
    this.supported.events[eventName].push({
      sinceInitMs: this.sinceInit(),
      args,
      mraidIndex,
      version: this.getMRAIDVersion(),
    });
  }

  /**
   * This function is used to wrap calls to mraid methods in a try/catch block.
   * Any error gets logged in this.supported.errors.
   *
   * In general, this.hasSupport() is used to check for support. This function
   * serves as a further safeguard against errors due to faulty mraid
   * implementations.
   */
  static _mraidTry(
    func,
    { tag = "other", fallback = undefined, logError = true }
  ) {
    try {
      return func();
    } catch (e) {
      if (logError) {
        // Record the error.
        this.supported.errors = this.supported.errors || [];
        let version;
        try {
          version = this.getMRAID().getVersion();
        } catch (ee) {
          console.error("Failed to get MRAID version", ee);
        }
        this.supported.errors.push({
          tag,
          sinceInitMs: this.sinceInit(),
          message: e != null ? e.message : null,
          mraidIndex: this.mraidIndex,
          version,
        });
      }
      return fallback;
    }
  }

  static _addMraidEventListener(eventName, callback) {
    if (!this.hasSupport("addEventListener")) return;

    const mraid = this.getMRAID();
    const mraidIndex = this.mraidIndex;

    this._mraidTry(
      () => {
        mraid.addEventListener(eventName, (...args) => {
          this._appendEventLog(eventName, mraidIndex, args);

          callback(...args);
        });
      },
      { tag: eventName }
    );
  }

  static _addEventListeners() {
    // "error" events include 2 arguments: a message and an action. The action
    // is the MRAID API call that caused the error.
    this._addMraidEventListener("error", (...args) => {
      const e = new Error(`MRAID error: ${args}`);
      this.emit("error", e);
    });
    this._addMraidEventListener("ready", () => {
      if (this.ready) return;
      this.ready = true;
      this.emit("ready");
    });
    this._addMraidEventListener("viewableChange", (viewable) => {
      this.isViewableByEvent = viewable;
      this.processViewableChange(
        this.isViewableByEvent && this.isViewportShown()
      );
    });

    // NOTE(oscar): We're using _addMraidEventListener to log this event.
    this._addMraidEventListener("exposureChange", () => {});

    /**
     * The following is a fallback in case mraid's viewableChange is not
     * supported.
     */
    document.addEventListener("visibilitychange", (...args) => {
      this._appendEventLog("document.visibilitychange", this.mraidIndex, args);

      /**
       * We delay the following by an event cycle to allow mraid's
       * viewableChange event to occur first.
       */
      runAsync(() => {
        if (this.supported.events.viewableChange == null) {
          this.isViewableByEvent = document.visibilityState !== "hidden";
          this.processViewableChange(
            this.isViewableByEvent && this.isViewportShown()
          );
        }
      });
    });
  }

  static processViewableChange(isViewable) {
    /**
     * NOTE(oscar): On the off chance the mraid implementation gives us
     * truthy/falsey values instead.
     */
    const normalizedIsViewable = !!isViewable;
    if (normalizedIsViewable === this.viewable) return;
    this.viewable = normalizedIsViewable;

    if (this.viewable) {
      this.lastViewableTS = Date.now();
      if (!this.isOrientationLocked && this.orientationToLock != null) {
        // NOTE(oscar): We won't lock the orientation until after the ad is
        // viewable.
        this.lockOrientation(this.orientationToLock);
      }
    } else {
      this.viewableDurationMS +=
        this.lastViewableTS == null ? 0 : Date.now() - this.lastViewableTS;
      this.lastOpenedURL = null;
      clearTimeout(this.clickRetryTimeout);
    }

    if (this.viewable) {
      this.emit("viewable");
    }
    this.emit("viewableChange", this.viewable);
  }

  static onReady(fn) {
    if (this.ready) {
      runAsync(fn);
      return;
    }
    this.once("ready", fn);
  }

  /**
   * Adds a listener to the "viewable" event. The "viewable" event fires
   * whenever we believe the ad has become visible to the user. If the ad was
   * already visible, then it fires the listener immediately.
   * @param {Function} callback
   * @param {boolean} continuous – Whether to listen to all viewable events.
   * Defaults to false, meaning it will only trigger the callback once.
   * @returns
   */
  static onViewable(callback, continuous = false) {
    if (this.viewable) {
      runAsync(callback);
      if (!continuous) return;
    }
    const deferrer = continuous ? this.on : this.once;
    deferrer("viewable", callback);
  }

  /** Runs fn now with current viewable value and on "viewableChange" event. */
  static nowAndOnViewableChange(fn) {
    runAsync(() => fn(this.viewable));

    this.on("viewableChange", fn);
  }

  // TODO(oscar): Replace with nowAndOnViewableChange once v2 productionizes.
  static onViewableChange(fn) {
    this.nowAndOnViewableChange(fn);
  }

  // IMPORTANT: This method MUST be called synchronously with a user action.
  static open(
    url,
    {
      // NOTE(Austin): Disable eslint for fields being unused, because
      // we don't want to have to keep removing/re-adding it.
      exchange,
      // eslint-disable-next-line
      platform,
      // eslint-disable-next-line
      abTests = [],
      isLiftoffURL = false,
      isAutoclick = false,
    }
  ) {
    // To prevent the click waterfall from resetting if the user clicks twice in
    // rapid succession, don't restart the waterfall unless the URL being opened
    // is different.
    if (this.lastOpenedURL === url) return;

    this.lastOpenedURL = url;
    clearTimeout(this.clickRetryTimeout);

    const mraidOpenIsSupported = this.hasSupport("open");
    const isMRAIDOnlyExchange = mraidOpenOnlyExchanges.has(exchange);
    const isAnchorOpenExchange = anchorOpenExchanges.has(exchange);
    const requireMRAID =
      mraidOpenIsSupported && (isAutoclick || isMRAIDOnlyExchange);

    const getFinalClickURL = (openMethod) => {
      return isLiftoffURL
        ? appendParamObjectToURL(url, {
            open_method: openMethod,
            tracking_method: TRACKING_METHOD_OPEN_METHOD,
          })
        : url;
    };
    const mraidOpen = (url) => {
      if (!this.ready) {
        this.emit(
          "error",
          new Error("Called mraid.open before mraid was ready")
        );
      }
      this.getMRAID().open(url);
    };
    const anchorOpen = (url) => {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.click();
    };
    const locationOpen = (url) => {
      window.location = url;
    };

    // To ensure that the user can click through, attempt multiple methods of
    // navigating to the URL. In order, they are:
    // 1. `mraid.open`, if mraid is available
    // 2. A fake anchor element
    // 3. `window.location`
    let openMethods = [
      {
        type: CLICK_OPEN_METHOD_MRAID,
        enabled: mraidOpenIsSupported,
        fn: mraidOpen,
      },
      {
        type: CLICK_OPEN_METHOD_ANCHOR,
        enabled: !requireMRAID,
        fn: anchorOpen,
      },
      {
        type: CLICK_OPEN_METHOD_LOCATION,
        enabled: !requireMRAID,
        fn: locationOpen,
      },
    ].filter(({ enabled }) => enabled);

    /**
     * Checks if we expect for the SKProductView will open. If so, then it's
     * likely for no visibility event to occur.
     * TODO(oscar/cwu): Replace url check with other `skproductview` indicating
     * boolean that should be populated from h2go.
     */
    const isOpenSKProductView = (openMethod, url) => {
      const appleStoreUrlPrefix = new RegExp("^(https?://)?apps\\.apple\\.com");
      return (
        openMethod.type === CLICK_OPEN_METHOD_MRAID &&
        appleStoreUrlPrefix.test(url)
      );
    };

    const tryOpen = (index = 0) => {
      // We don't emit any error if we're in the waterfall experiment
      // because this is guaranteed to happen if the user clicks more than
      // once (whereas in the original this will only happen if it thinks a
      // click failed).
      if (index > 0) {
        const methodType = openMethods[index - 1].type;
        this.emit(
          "error",
          new Warning(`Failed to open ${url} using ${methodType}`)
        );
      }
      if (index === openMethods.length - 1) {
        this.lastOpenedURL = null;
      }
      if (
        index >= openMethods.length &&
        !(index === 1 && isOpenSKProductView(openMethods[0], url))
      ) {
        this.emit(
          "error",
          new Error(
            `Failed to open ${url} by any methods. Tried ${openMethods.length} method(s)`
          )
        );
        return;
      }

      const openMethod = openMethods[index];
      const finalClickURL = getFinalClickURL(openMethod.type);
      openMethod.fn(finalClickURL);
      this.emit("clickMethodTried", {
        clickOpenMethod: openMethod.type,
        isAutoclick: isAutoclick,
      });
      Logger.recordMetricV2("click_url_open", {
        method: openMethod.type,
        index: index,
        total: openMethods.length,
        is_mraid_ready: this.ready,
      });

      this.clickRetryTimeout = setTimeout(
        tryOpen.bind(this, index + 1),
        CLICK_RETRY_INTERVAL_MS
      );
    };

    if (isMRAIDOnlyExchange) {
      mraidOpen(getFinalClickURL(CLICK_OPEN_METHOD_MRAID));
      Logger.recordMetricV2("click_url_open", {
        method: CLICK_OPEN_METHOD_MRAID,
        index: 0,
        total: 1,
        is_mraid_ready: this.ready,
      });
    } else if (isAnchorOpenExchange) {
      anchorOpen(getFinalClickURL(CLICK_OPEN_METHOD_ANCHOR));
      Logger.recordMetricV2("click_url_open", {
        method: CLICK_OPEN_METHOD_ANCHOR,
        index: 0,
        total: 1,
        is_mraid_ready: this.ready,
      });
    } else if (mraidOpenUncertainExchanges.has(exchange)) {
      // Keep these exchanges still in the click waterfall for now.
      tryOpen();
    } else if (abTests.includes("click-waterfall-removal-experiment")) {
      mraidOpen(getFinalClickURL(CLICK_OPEN_METHOD_MRAID));
      Logger.recordMetricV2("click_url_open", {
        method: CLICK_OPEN_METHOD_MRAID,
        index: 0,
        total: 1,
        is_mraid_ready: this.ready,
      });
    } else {
      tryOpen();
    }
  }

  static close() {
    this.onReady(() => {
      Logger.recordMetricV2("close_count", 1);

      if (!this.hasSupport("close")) {
        this.emit(
          "error",
          new Warning(
            "Attempted to call 'MRAID.close' when it is not supported."
          )
        );
        return;
      }

      this.getMRAID().close();
    });
  }

  // NOTE(brian): The `useCustomClose` method is deprecated (read: ignored) in
  // MRAID 3.0.
  // NOTE(Casey): Unity's `useCustomClose` throws an error. Although we handle
  // this case by not trying custom custom close on Unity, this implementation
  // handles that scenario in case other exchanges are (unknowingly) similar.
  /**
   * Returns a `Promise` that resolves with `false` if custom close was not set
   * or to `true` if custom close was set successfully. The `Promise` rejects
   * if an error was thrown.
   */
  static useCustomClose(use) {
    return new Promise((resolve, reject) => {
      try {
        this.onReady(() => {
          if (
            !(
              this.hasSupport("useCustomClose") &&
              this.hasSupport("close") &&
              use
            )
          ) {
            return resolve(false);
          }
          const mraid = this.getMRAID();
          mraid.useCustomClose(use);
          resolve(use);
        });
      } catch (e) {
        reject(e);
      }
    });
  }

  static getViewableDurationMS() {
    if (!this.viewable) return this.viewableDurationMS;
    if (this.lastViewableTS == null) {
      console.error(
        "Internal LiftoffMRAID error: this.lastViewableTS should be set."
      );
      return this.viewableDurationMS;
    }
    return this.viewableDurationMS + (Date.now() - this.lastViewableTS);
  }

  static lockOrientation(orientation) {
    if (
      !this.ready ||
      !this.viewable ||
      !this.hasSupport("setOrientationProperties")
    ) {
      return;
    }

    this.orientationToLock = orientation;
    const mraid = this.getMRAID();
    this._mraidTry(
      () => {
        mraid.setOrientationProperties({
          allowOrientationChange: false,
          forceOrientation: orientation,
        });
      },
      { tag: "setOrientationProperties" }
    );
    this.isOrientationLocked = true;
  }

  static getMRAID() {
    const windowMraid = window.mraid;
    if (windowMraid != null) {
      // NOTE(oscar): There are some sdks that will delete window.mraid after it
      // was initialized. In some cases at least, this is done to replace the
      // mraid object with a new one, but it's unclear why this is done in an
      // asynchronous way. In order not to race with the SDK's implementation of
      // MRAID, we will use the last mraid object loaded as a fallback.
      // Related test: https://docs.google.com/document/d/1TWLnTFlTGFc6ZJB_UXfR8QvmEa8x_d3k532fmX-13Fs
      // Related test 2: https://docs.google.com/document/d/1iChTG_zLZvQzI5Opw-6kpHqRCXRYklTPpbNB7TPBFdQ
      this.mraidIndex = this.mraids.indexOf(windowMraid);
      if (this.mraidIndex === -1) {
        this.mraidIndex = this.mraids.length;
        this.mraids.push(windowMraid);
      }
    }

    return this.mraids[this.mraidIndex];
  }

  static logMethodSupport(methodName, isSupported) {
    if (this.supported.methods[methodName] == null) {
      this.supported.methods[methodName] = {};
    }
    if (this.supported.methods[methodName][this.mraidIndex] == null) {
      this.supported.methods[methodName][this.mraidIndex] = [];
    }
    // NOTE(oscar): The MRAID spec allows for a method to go from no support to
    // support when the mraid's "ready" event triggers.
    this.supported.methods[methodName][this.mraidIndex].push({
      sinceInitMs: this.sinceInit(),
      ready: this.ready,
      support: isSupported,
      /** Logging is disabled here to prevent an infinite loop. */
      version: this.getMRAIDVersion({ log: false }),
    });
  }

  /**
   * Checks to see if the MRAID implementation supports the given method.
   * More specifically, this checks to see if the method is a function on the
   * window.mraid object. It does not actually call the method to see if it
   * works and any caller should be prepared to handle errors.
   *
   * @param {string} prop - The name of the method to check.
   * @param {Object} options
   * @param {boolean} options.log - Whether to log the result of the check.
   */
  static hasSupport(prop, { log = true } = {}) {
    const mraid = this.getMRAID();
    if (mraid == null) {
      return false;
    }

    if (prop === "mraid") {
      return true;
    }

    const support = type(mraid[prop]) === "function";

    if (log) {
      this.logMethodSupport(prop, support);
    }

    return support;
  }

  /** Returns the currently loaded MRAID version. */
  static getMRAIDVersion({ log = true } = {}) {
    return this._mraidTry(
      () => {
        return this.hasSupport("getVersion", { log })
          ? this.getMRAID().getVersion()
          : null;
      },
      { tag: "getVersion", logError: log }
    );
  }

  static isViewportShown() {
    return window.innerHeight > 0 && window.innerWidth > 0;
  }
}

if (window._LiftoffMRAID == null) {
  LiftoffMRAID.init();
  /**
   * NOTE(oscar): Global state is needed in order to only call .init() once and
   * to ensure that the same LiftoffMRAID is exported.
   */
  window._LiftoffMRAID = LiftoffMRAID;
}

export default window._LiftoffMRAID;



// WEBPACK FOOTER //
// ./src/js/madlib/liftoff_mraid.js